import { useEffect, useState, useMemo, useCallback } from 'react';
import { ChevronUpIcon, MapLayerIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import { IRP_MAP_ENABLED } from '@rsos/capstone/src/constants/permissionNames';
import BasemapTooltip from '@rsos/components/capstone/BasemapMenu/BasemapTooltip';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  allSpatialLayersSelector,
  filteredDynamicLayers,
} from '@rsos/components/capstone/common/Map/SpatialLayers/selectors';
import { CONNECTED_SITES_CATEGORY } from '@rsos/constants/dataTypeCategories';
import { useFlag } from '@rsos/flags';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { useAppDispatch, useAppSelector } from '@rsos/utils/reduxHooks';
import { clearCheckedLayers } from '../../actions/spatialLayers';
import useGISHelpers from '../../hooks/useGISHelpers';
import { getGISLayerID } from '../../utils/getGISLayerID';
import LayerCheckbox from './LayerCheckbox';
import { RegularCheckboxWrapper } from './LayerCheckbox.styles';
import LayerCheckboxGroup from './LayerCheckboxGroup';
import { LayerOpacityControl } from './LayerOpacityControl/LayerOpacityControl';
import {
  Controls,
  AccordionTitleWrapper,
  AccordionTitle,
  CollapsedIconWrapper,
  AccordionContent,
  ControlsBottom,
  SelectedLayersWrapper,
  SelectedText,
  ClearAllText,
} from './MapLayersControl.styles';
import { getGroupedlayers } from './helpers';

const MapLayersControl = () => {
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isGmrLandingZoneEnabled = useFlag('gmr_landing_zone');

  const {
    clearAllGISServices,
    gisMapLayersControlServices,
    hasEsriMapAccess,
    hasGISMapLayersControlServices,
    numActiveGISServices,
  } = useGISHelpers();
  const mapProvider = useAppSelector(state => state.sinatra.ui.mapProvider);

  const allSpatialLayers = useAppSelector(state =>
    allSpatialLayersSelector(state.spatialLayers),
  );
  const dynamicLayers = useAppSelector(
    state => state.spatialLayers.dynamicLayers,
  );
  const disabledDynamicLayers = useAppSelector(
    state => state.spatialLayers.disabledDynamicLayers,
  );
  const shouldDisplayDynamicLayers = !!filteredDynamicLayers(
    dynamicLayers,
    disabledDynamicLayers,
  ).length;
  const { irp_access_token } = useAppSelector(state => state.sinatra.irp);
  const permissions = useAppSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );

  const isIRPmode =
    permissions && permissions.includes(IRP_MAP_ENABLED) && irp_access_token;

  const isLayerDisabled = useMemo(() => mapProvider === GOOGLE, [mapProvider]);

  const [hoveredLayerId, setHoveredLayerId] = useState(null);

  const onHandleHoverLayer = layerID => setHoveredLayerId(layerID);

  // Check whether or not to render the component on page load or when receiving
  // dynamic spatial object data when the user doesn't have access to any static
  // spatial layers. This check is necessary because the user is able to receive
  // spatial layer data if the permission is enabled, but capability is disabled
  const shouldRenderMLControls = useMemo(() => {
    return Boolean(
      dynamicLayers?.length &&
        shouldDisplayDynamicLayers &&
        (!irp_access_token || isIRPmode),
    );
  }, [
    dynamicLayers?.length,
    irp_access_token,
    isIRPmode,
    shouldDisplayDynamicLayers,
  ]);

  const shouldRenderEsriMLControls = useMemo(() => {
    return hasGISMapLayersControlServices && (!irp_access_token || isIRPmode);
  }, [hasGISMapLayersControlServices, irp_access_token, isIRPmode]);

  const toggleAccordion = () => {
    setIsCollapsed(isCollapsed => !isCollapsed);
  };

  const handleClearAll = useCallback(() => {
    setIsCollapsed(true);
    if (hasEsriMapAccess) {
      dispatch(clearAllGISServices());
    }
    dispatch(clearCheckedLayers());
  }, [hasEsriMapAccess, dispatch, clearAllGISServices]);

  const [layerCheckboxes, setLayerCheckboxes] = useState([]);
  const [basemapLayerCheckboxes, setBasemapLayerCheckboxes] = useState([]);
  const [groupedLayerCheckboxes, setGroupedLayerCheckboxes] = useState({});

  const selectedCheckboxCount =
    numActiveGISServices + // will be 0 if hasEsriMapAccess is false
    layerCheckboxes.reduce((count, cb) => {
      if (cb?.layer?.isChecked) {
        return count + 1;
      }
      return count;
    }, 0) +
    Object.values(groupedLayerCheckboxes).reduce((count, cb) => {
      if (cb?.layers?.find(i => i.layer.isChecked)) {
        return count + 1;
      }
      return count;
    }, 0);

  // This effect is used to set the layer checkboxes for the map layers control.
  // When the spatial layers change, the checkboxes are updated to reflect the
  // new state of the spatial layers. Based on this premise, the count of
  // selected checkboxes can be made by reducing the layerCheckboxes state,
  // see selectedCheckboxCount above.
  useEffect(() => {
    // Removes placeholder layers that are not meant to be toggled or seen by users.
    const filterPlaceholders = layer => {
      return (
        layer.key !== 'avive_aed_placeholder' &&
        layer.key !== 'avive_aed_highlight' &&
        layer.category !== CONNECTED_SITES_CATEGORY &&
        (layer.key !== 'gmr_landing_zones' || isGmrLandingZoneEnabled)
      );
    };

    if (hasEsriMapAccess) {
      const { gisBasemapLayers, gisLayers } = gisMapLayersControlServices
        .map(layer => ({
          id: `${layer.id}`,
          key: `${layer.id}-${layer.display_name}`,
          layer,
          isGISSupplementalLayer: layer.service_type !== 0,
          isDisabled: isLayerDisabled,
        }))
        .reduce(
          (acc, layerObj) => {
            if (layerObj.layer.service_type === 0) {
              acc.gisBasemapLayers.push(layerObj);
            } else {
              acc.gisLayers.push(layerObj);
            }
            return acc;
          },
          { gisBasemapLayers: [], gisLayers: [] },
        );

      // Sort both arrays
      gisBasemapLayers.sort(
        (a, b) => b.layer?.layerIndex - a.layer?.layerIndex,
      );
      gisLayers.sort((a, b) => b.layer?.layerIndex - a.layer?.layerIndex);

      const spatialLayers = allSpatialLayers.map(layer => ({
        id: `${layer.id}-${layer.key}`,
        key: layer.key,
        category: layer.category?.key,
        layer,
        isGISSupplementalLayer: false,
        isDisabled: false,
      }));
      const groupedLayers = getGroupedlayers(spatialLayers);
      setGroupedLayerCheckboxes(groupedLayers);

      setBasemapLayerCheckboxes(gisBasemapLayers);
      setLayerCheckboxes([
        ...gisLayers,
        ...spatialLayers.filter(filterPlaceholders),
      ]);
    } else {
      const spatialLayers = allSpatialLayers.map(layer => ({
        id: layer.key,
        key: layer.key,
        category: layer.category?.key,
        layer,
        isGISSupplementalLayer: false,
        isDisabled: false,
      }));
      const groupedLayers = getGroupedlayers(spatialLayers);
      setGroupedLayerCheckboxes(groupedLayers);

      setLayerCheckboxes([...spatialLayers.filter(filterPlaceholders)]);
    }
  }, [
    hasEsriMapAccess,
    gisMapLayersControlServices,
    allSpatialLayers,
    mapProvider,
    isLayerDisabled,
    isGmrLandingZoneEnabled,
  ]);

  return (shouldRenderMLControls && shouldRenderEsriMLControls) ||
    layerCheckboxes.length > 0 ||
    Object.keys(groupedLayerCheckboxes).length > 0 ? (
    <Controls>
      <AccordionTitleWrapper onClick={toggleAccordion} horizontal>
        <MapLayerIcon color="primaryText" width={16} height={18} />
        <AccordionTitle data-name="map-layers-control-title">
          Map Layers
        </AccordionTitle>
        <CollapsedIconWrapper isCollapsed={isCollapsed}>
          <ChevronUpIcon color="secondaryLink" width={16} height={12} />
        </CollapsedIconWrapper>
      </AccordionTitleWrapper>
      {!isCollapsed && (
        <>
          <AccordionContent>
            {basemapLayerCheckboxes.map(cb => (
              <LayerCheckbox
                id={cb.id}
                isDisabled={cb.isDisabled}
                isGISSupplementalLayer={cb.isGISSupplementalLayer}
                key={cb.key}
                layer={cb.layer}
              />
            ))}
            {layerCheckboxes.map(cb => {
              const sublayers = cb.layer.sublayers || [];

              // Use sublayer ID if there's exactly one sublayer, otherwise use the parent layer ID
              const layerId =
                sublayers.length === 1
                  ? sublayers[0].sublayerID
                  : getGISLayerID(cb.layer);

              const isGisLayer = Boolean(cb.layer.gis_service_url);

              const shouldDisplayOpacityControl =
                sublayers.length <= 1 && isGisLayer;

              return (
                <div
                  key={cb.id}
                  onMouseEnter={() => onHandleHoverLayer(cb.id)}
                  onMouseLeave={() => onHandleHoverLayer(null)}
                >
                  <Popover
                    cssDisplayProperty="block"
                    isOpen={cb?.isDisabled ? cb.id === hoveredLayerId : false}
                    popoverID={`map-layers-control-popover-${cb.id}`}
                    trigger={
                      <Box horizontal>
                        <Box grow>
                          <LayerCheckbox
                            id={cb.layer.id}
                            isDisabled={cb.isDisabled}
                            isGISSupplementalLayer={cb.isGISSupplementalLayer}
                            key={cb.key}
                            layer={cb.layer}
                          />
                        </Box>
                        {shouldDisplayOpacityControl && (
                          <LayerOpacityControl
                            layerId={layerId}
                            layerName={cb.layer.display_name}
                          />
                        )}
                      </Box>
                    }
                    useCustomOffset
                    xOffset={0}
                    yOffset={24}
                  >
                    <BasemapTooltip />
                  </Popover>
                </div>
              );
            })}
            {Object.values(groupedLayerCheckboxes).map(group => (
              <RegularCheckboxWrapper key={group.id}>
                <LayerCheckboxGroup group={group} />
              </RegularCheckboxWrapper>
            ))}
          </AccordionContent>
          <ControlsBottom>
            <SelectedLayersWrapper>
              <SelectedText data-name="num-selected-layers">
                {selectedCheckboxCount} Selected
              </SelectedText>
              <ClearAllText
                data-name="clear-all-layers"
                onClick={handleClearAll}
              >
                Clear All
              </ClearAllText>
            </SelectedLayersWrapper>
          </ControlsBottom>
        </>
      )}
    </Controls>
  ) : null;
};

export default MapLayersControl;
